/**
 * Checkbox Field Component
 * Wrapper for MUI Checkbox with react-hook-form integration
 */
import React from 'react'
import {
  FormControlLabel,
  Checkbox,
  FormHelperText,
  FormControl,
  Typography
} from '@mui/material'
import { Controller } from 'react-hook-form'
import { FormFieldProps } from '../types'
import { useFormContext } from '../FormContext'

const CheckboxField: React.FC<FormFieldProps> = ({
  config,
  control,
  errors,
  readOnly
}) => {
  const { name, label, disabled, helperText, fieldProps = {} } = config

  // get the original values from the form context for change detection
  const formContext = useFormContext()
  const formValues = formContext?.values || {}

  // check if the field is changed
  const isFieldChanged = () => {
    // check the field level configuration, if the change highlighting is not enabled, return false
    if (config.enableChangeHighlight !== true) {
      return false
    }

    // if the change highlighting is enabled, check if the field is changed
    if (formContext) {
      const originalValues = formContext.originalValues || {}
      const currentValues = formValues

      // compare the original value and the current value
      const originalValue = originalValues[name]
      const currentValue = currentValues[name]

      // handle the boolean value
      const origBoolValue =
        originalValue === true ||
        originalValue === 'true' ||
        originalValue === 1
      const currBoolValue =
        currentValue === true || currentValue === 'true' || currentValue === 1

      // only return true when the value is really different
      return origBoolValue !== currBoolValue
    }
    return false
  }

  // check if the field is changed
  const isChanged = isFieldChanged()

  // check if the field is read only
  const isReadOnly =
    typeof readOnly === 'boolean'
      ? readOnly
      : Array.isArray(readOnly)
        ? readOnly.includes(name)
        : false

  // create the styled label
  const styledLabel = isChanged ? (
    <Typography
      component='span'
      sx={{
        color: 'secondary.dark',
        fontWeight: 'bold'
      }}>
      {label}
    </Typography>
  ) : (
    label
  )

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ...field } }) => (
        <FormControl error={!!errors[name]}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                disabled={disabled || isReadOnly}
                {...field}
                {...fieldProps}
              />
            }
            label={styledLabel}
          />
          {(!!errors[name] || helperText) && (
            <FormHelperText>
              {errors[name]?.message || helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  )
}

export default CheckboxField
